import { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Phone, Loader2, CloudOff, MapPin, Plus, X, Check, AlertTriangle, Clock, User, ArrowRight, MessageSquare, Circle, CheckCircle2, FileText, Eye, ChevronDown, Briefcase, Trash2 } from 'lucide-react';
import { api } from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { formatCOP, formatDate } from '@/lib/utils';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { LeadListings } from '@/components/LeadListings';
import { FollowUpDialog, FOLLOW_UP_STAGES } from '@/components/FollowUpDialog';
import { DesiredFeaturesPicker } from '@/components/DesiredFeaturesPicker';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { toast } from 'sonner';

const STAGES = [
  { key: 'lead_nuevo', label: 'Lead Nuevo', color: '#3B82F6' },
  { key: 'contactado', label: 'Contactado', color: '#8B5CF6' },
  { key: 'calificado', label: 'Calificado', color: '#F59E0B' },
  { key: 'en_visitas', label: 'En Visitas', color: '#10B981' },
  { key: 'en_negociacion', label: 'En Negociacion', color: '#EF4444' },
  { key: 'cerrado', label: 'Cerrado', color: '#6BCB77' },
  { key: 'perdido', label: 'Perdido', color: '#6B7280' },
  { key: 'propietario', label: 'Propietarios', color: '#A38560' },
];
const LEAD_SOURCES = ["Pauta", "Referido", "RRSS Sebastian", "RRSS propias", "Meta Ads", "Pagina Web", "WhatsApp", "Llamada", "Otras"];
const LOST_REASONS = ["Duplicado","No contesta","Descartado (legal, presupuesto, desiste de comprar)","Error en el numero","Cerró por otro lado"];

const pick = (obj, keys) => Object.fromEntries([...keys].map(k => [k, obj[k]]));

const AutosaveStatus = ({ status, onRetry }) => {
  if (status === 'error') return (
    <button onClick={onRetry} data-testid="autosave-status" className="flex items-center gap-1.5 text-xs text-red-400 px-3 py-1.5 rounded-lg border border-red-400/30 hover:bg-red-400/10">
      <CloudOff size={13} />No se guardó · Reintentar
    </button>
  );
  const label = { pending: 'Guardando...', saving: 'Guardando...', saved: 'Guardado', idle: 'Se guarda solo' }[status];
  return (
    <span data-testid="autosave-status" className={`flex items-center gap-1.5 text-xs ${status === 'saved' ? 'text-[#6BCB77]' : 'text-white/40'}`}>
      {(status === 'pending' || status === 'saving') ? <Loader2 size={13} className="animate-spin" /> : <Check size={13} />}{label}
    </span>
  );
};

const FollowUpCard = ({ lead, onChange }) => {
  if (!FOLLOW_UP_STAGES.includes(lead.stage)) return null;
  const d = lead.next_follow_up_date;
  const today = new Date().toLocaleDateString('en-CA');
  const tone = !d ? 'amber' : d < today ? 'red' : d === today ? 'yellow' : 'green';
  const cls = { amber: 'bg-amber-500/10 border-amber-500/30 text-amber-200', red: 'bg-red-500/10 border-red-500/30 text-red-300',
    yellow: 'bg-yellow-400/10 border-yellow-400/30 text-yellow-200', green: 'bg-[#16302B] border-white/10 text-white/80' }[tone];
  const label = d ? new Date(d + 'T12:00:00').toLocaleDateString('es-CO', { weekday: 'long', day: 'numeric', month: 'long', ...(d.slice(0, 4) !== today.slice(0, 4) ? { year: 'numeric' } : {}) }) + (lead.next_follow_up_time ? ` · ${lead.next_follow_up_time}` : '') : '';
  return (
    <div data-testid="lead-follow-up-card" className={`flex items-center gap-3 p-3 rounded-xl border ${cls}`}>
      <Clock size={16} className="shrink-0" />
      <p className="text-sm flex-1 min-w-0">
        {!d ? <b>Este lead no tiene fecha de siguiente seguimiento</b> : <>
          <span className="text-[10px] uppercase tracking-wider opacity-70 block">{tone === 'red' ? 'Seguimiento vencido' : tone === 'yellow' ? 'Seguimiento hoy' : 'Próximo seguimiento'}</span>
          <span data-testid="lead-follow-up-date" className="font-medium capitalize">{label}</span>{lead.next_follow_up_title && <span className="opacity-70"> — {lead.next_follow_up_title}</span>}</>}
      </p>
      <button onClick={onChange} data-testid="lead-follow-up-change" className="shrink-0 text-xs px-3 py-1.5 rounded-lg bg-white/10 hover:bg-white/15 text-white">{d ? 'Cambiar' : 'Agendar'}</button>
    </div>
  );
};

export default function LeadDetail() {
  const { user } = useAuth();
  const { id } = useParams();
  const navigate = useNavigate();
  const [lead, setLead] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [properties, setProperties] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [brokers, setBrokers] = useState([]);
  const [sectors, setSectors] = useState([]);
  const [customFields, setCustomFields] = useState([]);
  const [linkedDeals, setLinkedDeals] = useState([]);
  const [saveStatus, setSaveStatus] = useState('idle');
  const dirtyRef = useRef(new Set());
  const editFormRef = useRef({});
  const [newPropUrl, setNewPropUrl] = useState('');
  const [newTask, setNewTask] = useState({ title: '', description: '', due_date: '' });
  const [showNewSector, setShowNewSector] = useState(false);
  const [newSectorName, setNewSectorName] = useState('');
  const [lostModal, setLostModal] = useState(false);
  const [lostReason, setLostReason] = useState('');
  const [activityType, setActivityType] = useState('contact_attempt');
  const [activityMessage, setActivityMessage] = useState('');
  const [expandedEntry, setExpandedEntry] = useState(null);
  const [myTags, setMyTags] = useState([]);
  const [tagPickerOpen, setTagPickerOpen] = useState(false);
  const [newTagName, setNewTagName] = useState('');
  const [newTagColor, setNewTagColor] = useState('#A38560');
  const [templates, setTemplates] = useState([]);
  const [showTplModal, setShowTplModal] = useState(false);
  const [newTpl, setNewTpl] = useState({ title: '', content: '', type: 'note' });
  const [metaEvents, setMetaEvents] = useState([]);
  const [showMeta, setShowMeta] = useState(false);

  const loadMeta = useCallback(async () => {
    try { setMetaEvents(await api.get(`/meta/events?lead_id=${id}&limit=50`)); } catch { /* noop */ }
  }, [id]);

  const resyncMeta = async () => {
    try {
      const r = await api.post(`/meta/leads/${id}/sync`);
      const okCount = (r.events || []).filter(e => e.status === 'sent').length;
      okCount ? toast.success(`${okCount} evento(s) enviados a Meta`) : toast.error((r.events || [])[0]?.detail || 'Meta no acepto los eventos');
      loadMeta();
    } catch (e) { toast.error(e?.response?.data?.detail || 'No se pudo sincronizar'); }
  };

  const loadTagsTpls = useCallback(async () => {
    try {
      const [tg, tp] = await Promise.all([api.get('/broker-tags'), api.get('/activity-templates')]);
      setMyTags(tg); setTemplates(tp);
    } catch { /* ignore */ }
  }, []);

  const createTag = async () => {
    const name = newTagName.trim();
    if (!name) return toast.error('Nombre requerido');
    try {
      const t = await api.post('/broker-tags', { name, color: newTagColor });
      setMyTags(prev => [...prev, t].sort((a, b) => a.name.localeCompare(b.name)));
      setNewTagName(''); toast.success('Etiqueta creada');
    } catch (e) { toast.error(e?.response?.data?.detail || 'Error'); }
  };

  const attachTag = async (tag) => {
    try {
      const added = await api.post(`/crm/leads/${id}/tags`, { name: tag.name, color: tag.color });
      setLead(prev => ({ ...prev, tags: [...(prev.tags || []), added] }));
      setTagPickerOpen(false);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Error al asignar'); }
  };

  const removeTagFromLead = async (tagId) => {
    try {
      await api.delete(`/crm/leads/${id}/tags/${tagId}`);
      setLead(prev => ({ ...prev, tags: (prev.tags || []).filter(t => t.id !== tagId) }));
    } catch (e) { toast.error(e?.response?.data?.detail || 'Error'); }
  };

  const deleteMyTag = async (tagId) => {
    if (!window.confirm('Eliminar esta etiqueta de tu paleta? Los leads que ya la tengan la conservan.')) return;
    try { await api.delete(`/broker-tags/${tagId}`); setMyTags(prev => prev.filter(t => t.id !== tagId)); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Error'); }
  };

  const saveTemplate = async () => {
    if (!newTpl.title.trim() || !newTpl.content.trim()) return toast.error('Titulo y contenido requeridos');
    try {
      const tpl = await api.post('/activity-templates', newTpl);
      setTemplates(prev => [tpl, ...prev]);
      setNewTpl({ title: '', content: '', type: newTpl.type });
      toast.success('Plantilla guardada');
    } catch (e) { toast.error(e?.response?.data?.detail || 'Error'); }
  };

  const deleteTemplate = async (tid) => {
    if (!window.confirm('Eliminar plantilla?')) return;
    try { await api.delete(`/activity-templates/${tid}`); setTemplates(prev => prev.filter(t => t.id !== tid)); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Error'); }
  };

  const applyTemplate = (tpl) => {
    setActivityMessage(tpl.content);
    setActivityType(tpl.type || 'note');
  };

  const createSector = async () => {
    const name = newSectorName.trim();
    if (!name) return;
    try { await api.post('/sectors', { name }); setSectors(prev => [...prev, name].sort()); upd('sector', name); setNewSectorName(''); setShowNewSector(false); toast.success('Sector creado'); }
    catch { toast.error('Error al crear sector'); }
  };

  const load = useCallback(async () => {
    try {
      const [l, b, s, cf] = await Promise.all([api.get(`/crm/leads/${id}`), api.get('/users/brokers'), api.get('/sectors'), api.get('/crm/custom-fields')]);
      setLead(l); setEditForm(prev => ({ ...l, ...pick(prev, dirtyRef.current) })); setBrokers(b); setSectors(s); setCustomFields(cf);
    } catch { toast.error('Error al cargar lead'); }
  }, [id]);

  const loadExtras = useCallback(async () => {
    try {
      const [p, t, d] = await Promise.all([api.get(`/crm/leads/${id}/properties`), api.get(`/crm/leads/${id}/tasks`), api.get(`/crm/leads/${id}/deals`)]);
      setProperties(p); setTasks(t); setLinkedDeals(d);
    } catch {}
  }, [id]);

  useEffect(() => { load(); loadExtras(); loadTagsTpls(); loadMeta(); }, [load, loadExtras, loadTagsTpls, loadMeta]);

  const upd = (key, val) => { dirtyRef.current.add(key); setEditForm(prev => ({ ...prev, [key]: val })); setSaveStatus('pending'); };

  const saveLead = useCallback(async () => {
    const keys = [...dirtyRef.current];
    if (!keys.length) return;
    const payload = pick(editFormRef.current, new Set(keys));
    keys.forEach(k => dirtyRef.current.delete(k));
    setSaveStatus('saving');
    try {
      const updated = await api.put(`/crm/leads/${id}`, payload);
      setLead(updated);
      setSaveStatus(dirtyRef.current.size ? 'pending' : 'saved');
    } catch {
      keys.forEach(k => dirtyRef.current.add(k));
      setSaveStatus('error');
    }
  }, [id]);

  useEffect(() => { editFormRef.current = editForm; }, [editForm]);
  useEffect(() => {
    if (saveStatus !== 'pending') return;
    const t = setTimeout(saveLead, 900);
    return () => clearTimeout(t);
  }, [editForm, saveStatus, saveLead]);
  useEffect(() => {
    const flush = () => {
      if (!dirtyRef.current.size) return;
      fetch(`${process.env.REACT_APP_BACKEND_URL}/api/crm/leads/${id}`, {
        method: 'PUT', keepalive: true,
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${sessionStorage.getItem('selecto_token')}` },
        body: JSON.stringify(pick(editFormRef.current, dirtyRef.current)),
      }).catch(() => {});
      dirtyRef.current.clear();
    };
    window.addEventListener('beforeunload', flush);
    window.addEventListener('pagehide', flush);
    return () => { window.removeEventListener('beforeunload', flush); window.removeEventListener('pagehide', flush); flush(); };
  }, [id]);

  const [askFollowUp, setAskFollowUp] = useState(false);
  const requireFollowUp = (stage) => { if (FOLLOW_UP_STAGES.includes(stage)) setAskFollowUp(true); };

  const changeStage = async (stage) => {
    if (stage === 'perdido') { setLostModal(true); setLostReason(''); return; }
    try { await api.put(`/crm/leads/${id}/stage`, { stage }); toast.success('Etapa actualizada'); load(); requireFollowUp(stage); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Error al cambiar etapa'); }
  };

  const confirmLost = async () => {
    if (!lostReason) return toast.error('Selecciona un motivo');
    try { await api.put(`/crm/leads/${id}/stage`, { stage: 'perdido', lost_reason: lostReason }); setLostModal(false); toast.success('Movido a Perdido'); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Error'); }
  };

  const addActivity = async () => {
    if (!activityMessage.trim()) return toast.error('Escribe un mensaje');
    try {
      await api.post(`/crm/leads/${id}/activity`, { type: activityType, message: activityMessage });
      setActivityMessage('');
      toast.success(activityType === 'contact_attempt' ? 'Intento registrado' : 'Nota agregada');
      load(); loadExtras(); requireFollowUp(lead.stage);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Error'); }
  };

  const toggleResponded = async (entryId) => {
    try {
      await api.put(`/crm/leads/${id}/activity/${entryId}/respond`);
      toast.success('Respondio — intentos reseteados');
      load(); loadExtras(); requireFollowUp(lead.stage);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Error'); }
  };

  const deleteActivity = async (entryId) => {
    try { await api.delete(`/crm/leads/${id}/activity/${entryId}`); load(); toast.success('Actividad eliminada'); }
    catch { toast.error('Error'); }
  };

  const addProp = async () => { if (!newPropUrl.trim()) return; try { await api.post(`/crm/leads/${id}/properties`, { url: newPropUrl }); setNewPropUrl(''); loadExtras(); } catch { toast.error('Error'); } };
  const addTask = async () => { if (!newTask.title.trim()) return; try { await api.post(`/crm/leads/${id}/tasks`, newTask); setNewTask({ title: '', description: '', due_date: '' }); loadExtras(); load(); toast.success('Tarea creada'); } catch { toast.error('Error'); } };
  const toggleTask = async (t) => { try { await api.put(`/crm/leads/${id}/tasks/${t.id}`, { completed: !t.completed }); loadExtras(); load(); if (!t.completed) requireFollowUp(lead.stage); } catch {} };

  if (!lead) return <div className="flex items-center justify-center h-64"><div className="animate-spin w-8 h-8 border-2 border-[#A38560] border-t-transparent rounded-full" /></div>;

  const inputCls = "bg-[#0A1F1A] border-[#1A3B32] text-white placeholder:text-white/30 focus:border-[#A38560] h-11 rounded-xl";
  const selectContentCls = "bg-[#0A1F1A] border-[#1A3B32] text-white";
  const maxAttempts = lead.max_attempts || { lead_nuevo: 3, contactado: 3, calificado: 4, en_visitas: 6, en_negociacion: 6 }[lead.stage] || 0;
  const attemptCount = (lead.activity_log || []).filter(e => e.type === 'contact_attempt' && !e.responded).length;
  const allAttemptsUsed = maxAttempts > 0 && attemptCount >= maxAttempts;

  return (
    <div className="space-y-4 max-w-4xl mx-auto">
      <div className="flex items-center gap-3">
        <button onClick={() => navigate('/crm')} className="text-white/50 hover:text-white transition-colors"><ArrowLeft size={20} /></button>
        <h1 className="font-outfit text-xl font-semibold text-white flex-1 truncate">{lead.full_name}</h1>
        <AutosaveStatus status={saveStatus} onRetry={saveLead} />
      </div>

      {/* Tags */}
      <div className="bg-[#0A1F1A] rounded-2xl border border-white/5 p-4">
        <div className="flex items-center justify-between mb-2">
          <span className="text-[11px] uppercase tracking-wider text-white/40">Etiquetas</span>
          <button onClick={() => setTagPickerOpen(!tagPickerOpen)} className="text-[11px] text-[#A38560] hover:underline" data-testid="toggle-tag-picker">
            {tagPickerOpen ? 'Cerrar' : '+ Agregar / Gestionar'}
          </button>
        </div>
        <div className="flex flex-wrap gap-1.5">
          {(lead.tags || []).map(t => (
            <span key={t.id} className="inline-flex items-center gap-1.5 text-[11px] px-2.5 py-1 rounded-full font-medium" style={{ backgroundColor: t.color + '22', color: t.color, border: `1px solid ${t.color}55` }}>
              <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: t.color }}></span>
              {t.name}
              <button onClick={() => removeTagFromLead(t.id)} className="hover:text-white/90 -mr-0.5">×</button>
            </span>
          ))}
          {!(lead.tags && lead.tags.length) && <span className="text-[11px] text-white/30 italic">Sin etiquetas</span>}
        </div>
        {tagPickerOpen && (
          <div className="mt-3 pt-3 border-t border-white/5 space-y-3">
            {myTags.length > 0 && (
              <div>
                <p className="text-[10px] uppercase tracking-wider text-white/40 mb-1.5">Mi paleta</p>
                <div className="flex flex-wrap gap-1.5">
                  {myTags.map(t => (
                    <div key={t.id} className="inline-flex items-center gap-1 text-[11px] px-2 py-1 rounded-full border" style={{ backgroundColor: t.color + '22', color: t.color, borderColor: t.color + '55' }}>
                      <button onClick={() => attachTag(t)} className="hover:underline">{t.name}</button>
                      <button onClick={() => deleteMyTag(t.id)} className="opacity-50 hover:opacity-100 text-[9px]">🗑</button>
                    </div>
                  ))}
                </div>
              </div>
            )}
            <div className="flex gap-2">
              <input value={newTagName} onChange={e => setNewTagName(e.target.value)} placeholder="Nueva etiqueta..."
                className="flex-1 bg-[#03110D] border border-white/10 rounded-lg px-2 py-1.5 text-xs text-white placeholder:text-white/30" data-testid="new-tag-name" />
              <input type="color" value={newTagColor} onChange={e => setNewTagColor(e.target.value)}
                className="w-9 h-9 bg-transparent border border-white/10 rounded-lg cursor-pointer" />
              <button onClick={createTag} className="px-3 py-1.5 bg-[#A38560]/20 text-[#A38560] border border-[#A38560]/30 rounded-lg text-xs" data-testid="create-tag-btn">Crear</button>
            </div>
          </div>
        )}
      </div>

      {/* Pipeline */}
      <div className="flex gap-1 overflow-x-auto pb-1">
        {STAGES.map(s => (
          <button key={s.key} onClick={() => changeStage(s.key)} data-testid={`stage-btn-${s.key}`}
            className={`px-3 py-1.5 rounded-lg text-xs whitespace-nowrap transition-all ${lead.stage === s.key ? 'text-white font-medium' : 'text-white/40 hover:text-white/70 bg-white/5'}`}
            style={lead.stage === s.key ? { backgroundColor: s.color } : {}}>
            {s.label}
          </button>
        ))}
      </div>

      <FollowUpCard lead={lead} onChange={() => setAskFollowUp('reschedule')} />
      <FollowUpDialog lead={askFollowUp ? lead : null} reschedule={askFollowUp === 'reschedule'} onDone={() => { setAskFollowUp(false); load(); loadExtras(); }} />

      {/* Pool retention */}
      {lead.is_pool && lead.pool_claimed_by && !lead.pool_attended && lead.pool_deadline_at && (
        <div data-testid="pool-retention-banner" className="flex items-center gap-2 p-3 bg-amber-500/10 border border-amber-500/25 rounded-xl">
          <Clock size={16} className="text-amber-400 shrink-0" />
          <p className="text-xs text-amber-200/90">
            Lead reclamado del pool. Si no registras actividad antes del{' '}
            <b>{new Date(lead.pool_deadline_at).toLocaleString('es-CO')}</b>, volvera al pool automaticamente.
          </p>
        </div>
      )}
      {lead.pool_return_count > 0 && (
        <p className="text-[11px] text-white/40 px-1" data-testid="pool-return-count">
          Este lead ha vuelto al pool {lead.pool_return_count} vez(ces) por inactividad
          {lead.pool_escalated ? ' · escalado a administracion' : ''}
        </p>
      )}

      {/* Meta feedback */}
      <div className="bg-[#0A1F1A] rounded-2xl border border-white/5 p-3 lg:p-4">
        <div className="flex items-center justify-between gap-2">
          <button onClick={() => setShowMeta(v => !v)} data-testid="toggle-meta-panel"
            className="flex items-center gap-2 text-xs uppercase tracking-widest text-white/40 hover:text-white/70">
            <ChevronDown size={14} className={`transition-transform ${showMeta ? 'rotate-180' : ''}`} />
            Feedback a Meta ({metaEvents.length})
          </button>
          <span className="ml-2 text-[10px] px-2 py-0.5 rounded-full border shrink-0" data-testid="lead-attribution-badge"
            style={lead.attribution === 'pauta'
              ? { background: 'rgba(107,203,119,0.1)', color: '#6BCB77', borderColor: 'rgba(107,203,119,0.25)' }
              : { background: 'rgba(245,158,11,0.1)', color: '#FCD34D', borderColor: 'rgba(245,158,11,0.25)' }}>
            {lead.attribution === 'pauta' ? 'Pauta' : 'Organico'}
          </span>
          {user?.role === 'admin' && (
            <button onClick={resyncMeta} data-testid="meta-resync-btn"
              className="text-[11px] px-2.5 py-1 rounded-lg bg-[#A38560]/15 text-[#A38560] border border-[#A38560]/30">Reenviar a Meta</button>
          )}
        </div>
        {showMeta && (
          <div className="mt-3 space-y-1.5" data-testid="meta-lead-events">
            {metaEvents.map(ev => (
              <div key={ev.id} className="flex items-start gap-2 text-[11px] bg-[#16302B] rounded-lg border border-white/5 px-2.5 py-2">
                <span className={`w-1.5 h-1.5 rounded-full shrink-0 mt-1.5 ${ev.status === 'sent' ? 'bg-[#6BCB77]' : ev.status === 'error' ? 'bg-red-400' : 'bg-white/20'}`} />
                <div className="min-w-0">
                  <p className="text-white/80">{ev.event_name || ev.stage} <span className="text-white/30">· {new Date(ev.created_at).toLocaleString('es-CO')}</span></p>
                  <p className="text-white/35 break-words">{ev.detail}</p>
                </div>
              </div>
            ))}
            {metaEvents.length === 0 && <p className="text-[11px] text-white/30 py-2">Sin eventos enviados a Meta para este lead</p>}
          </div>
        )}
      </div>

      {/* Attempt count indicator */}
      {maxAttempts > 0 && !['cerrado', 'perdido'].includes(lead.stage) && (
        <div className="flex items-center gap-3 px-1">
          <span className="text-xs text-white/40">Intentos sin respuesta:</span>
          <div className="flex items-center gap-1">
            {Array.from({ length: maxAttempts }).map((_, i) => (
              <div key={i} className={`w-2.5 h-2.5 rounded-full ${i < attemptCount ? 'bg-red-400' : 'bg-white/10'}`} />
            ))}
          </div>
          <span className="text-xs text-white/30">{attemptCount}/{maxAttempts}</span>
        </div>
      )}

      {/* Failed attempts banner */}
      {allAttemptsUsed && lead.stage !== 'perdido' && (
        <div className="flex items-center justify-between p-3 bg-red-500/10 border border-red-500/20 rounded-xl">
          <div className="flex items-center gap-2">
            <AlertTriangle size={18} className="text-red-400" />
            <p className="text-sm text-red-400 font-medium">Maximo de intentos alcanzado. Mover a Perdido?</p>
          </div>
          <button onClick={() => { setLostModal(true); setLostReason(''); }} data-testid="move-to-lost-btn"
            className="px-4 py-1.5 bg-red-500 text-white text-sm font-medium rounded-lg">Mover a Perdido</button>
        </div>
      )}

      {/* Stage Timeline */}
      {lead.stage_history && lead.stage_history.length > 0 && (
        <div className="bg-[#0A1F1A] rounded-2xl border border-white/5 p-3 lg:p-4">
          <p className="text-xs uppercase tracking-widest text-white/40 mb-3">Tiempo en cada etapa</p>
          <div className="flex flex-wrap gap-2">
            {lead.stage_history.map((entry, i) => {
              const stageInfo = STAGES.find(s => s.key === entry.stage);
              const enteredAt = new Date(entry.entered_at);
              const exitedAt = i < lead.stage_history.length - 1 ? new Date(lead.stage_history[i + 1].entered_at) : new Date();
              const diffMs = exitedAt - enteredAt;
              const days = Math.floor(diffMs / 86400000);
              const hours = Math.floor((diffMs % 86400000) / 3600000);
              const isActive = i === lead.stage_history.length - 1;
              const timeLabel = days > 0 ? `${days}d ${hours}h` : `${hours}h`;
              return (
                <div key={i} className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs ${isActive ? 'border border-white/10' : 'bg-white/5'}`}>
                  <span className="w-2 h-2 rounded-full shrink-0" style={{ backgroundColor: stageInfo?.color || '#666' }} />
                  <span className="text-white/60">{stageInfo?.label || entry.stage}</span>
                  <span className={`font-mono font-medium ${isActive ? 'text-[#A38560]' : 'text-white/40'}`}>{timeLabel}</span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      <Tabs defaultValue="datos" className="w-full">
        <TabsList className="bg-[#0A1F1A] border border-[#1A3B32] rounded-xl p-1 w-full flex overflow-x-auto">
          <TabsTrigger value="datos" className="data-[state=active]:bg-[#A38560] data-[state=active]:text-[#03110D] text-white/60 gap-1.5 text-xs rounded-lg flex-1 min-w-[60px] py-2">Datos</TabsTrigger>
          <TabsTrigger value="tareas" className="data-[state=active]:bg-[#A38560] data-[state=active]:text-[#03110D] text-white/60 gap-1.5 text-xs rounded-lg flex-1 min-w-[60px] py-2">Tareas</TabsTrigger>
          <TabsTrigger value="inmuebles" className="data-[state=active]:bg-[#A38560] data-[state=active]:text-[#03110D] text-white/60 gap-1.5 text-xs rounded-lg flex-1 min-w-[60px] py-2">Inmuebles</TabsTrigger>
          <TabsTrigger value="negocios" className="data-[state=active]:bg-[#A38560] data-[state=active]:text-[#03110D] text-white/60 gap-1.5 text-xs rounded-lg flex-1 min-w-[60px] py-2">Negocios</TabsTrigger>
          <TabsTrigger value="actividad" data-testid="tab-actividad" className="data-[state=active]:bg-[#A38560] data-[state=active]:text-[#03110D] text-white/60 gap-1.5 text-xs rounded-lg flex-1 min-w-[60px] py-2">Actividad</TabsTrigger>
          <TabsTrigger value="listings" data-testid="tab-listings" className="data-[state=active]:bg-[#A38560] data-[state=active]:text-[#03110D] text-white/60 gap-1.5 text-xs rounded-lg flex-1 min-w-[60px] py-2">Listings</TabsTrigger>
        </TabsList>

        {/* DATOS TAB */}
        <TabsContent value="datos">
          <div className="bg-[#0A1F1A] rounded-2xl border border-white/5 p-4 lg:p-6 mt-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div><Label className="text-white/50 text-xs uppercase tracking-wider mb-1.5 block">Nombre</Label>
                <Input value={editForm.full_name || ''} onChange={e => upd('full_name', e.target.value)} className={inputCls} /></div>
              <div><Label className="text-white/50 text-xs uppercase tracking-wider mb-1.5 block">Telefono</Label>
                <Input value={editForm.phone || ''} onChange={e => upd('phone', e.target.value)} className={inputCls} /></div>
              <div><Label className="text-white/50 text-xs uppercase tracking-wider mb-1.5 block">Email</Label>
                <Input value={editForm.email || ''} onChange={e => upd('email', e.target.value)} className={inputCls} /></div>
              <div><Label className="text-white/50 text-xs uppercase tracking-wider mb-1.5 block">Tipo de Cliente</Label>
                <Select value={editForm.client_type || ''} onValueChange={v => upd('client_type', v)}>
                  <SelectTrigger className={inputCls} data-testid="lead-client-type"><SelectValue placeholder="Seleccionar..." /></SelectTrigger>
                  <SelectContent className={selectContentCls}>
                    <SelectItem value="Comprador">Comprador</SelectItem>
                    <SelectItem value="Arrendatario">Arrendatario</SelectItem>
                    <SelectItem value="Propietario">Propietario</SelectItem>
                  </SelectContent>
                </Select></div>
              <div><Label className="text-white/50 text-xs uppercase tracking-wider mb-1.5 block">Fuente</Label>
                <Select value={editForm.source || ''} onValueChange={v => upd('source', v)}>
                  <SelectTrigger className={inputCls}><SelectValue /></SelectTrigger>
                  <SelectContent className={selectContentCls}>{LEAD_SOURCES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                </Select></div>
              <div className="sm:col-span-2"><Label className="text-white/50 text-xs uppercase tracking-wider mb-1.5 block">Zonas de Interes</Label>
                <div className="flex flex-wrap gap-1.5 mb-2">
                  {(editForm.sectors || (editForm.sector ? editForm.sector.split(',').map(s => s.trim()).filter(Boolean) : [])).map(s => (
                    <span key={s} className="inline-flex items-center gap-1 px-2 py-1 bg-[#A38560]/10 text-[#A38560] text-xs rounded-lg border border-[#A38560]/20">
                      {s} <button type="button" onClick={() => { const current = editForm.sectors || (editForm.sector ? editForm.sector.split(',').map(x => x.trim()).filter(Boolean) : []); const updated = current.filter(x => x !== s); upd('sectors', updated); upd('sector', updated.join(', ')); }} className="hover:text-white"><X size={10} /></button>
                    </span>
                  ))}
                </div>
                {!showNewSector ? (
                  <div className="flex gap-1.5">
                    <Select value="" onValueChange={v => { if (!v) return; const current = editForm.sectors || (editForm.sector ? editForm.sector.split(',').map(x => x.trim()).filter(Boolean) : []); if (!current.includes(v)) { const updated = [...current, v]; upd('sectors', updated); upd('sector', updated.join(', ')); } }}>
                      <SelectTrigger className={`${inputCls} flex-1`}><SelectValue placeholder="Agregar zona..." /></SelectTrigger>
                      <SelectContent className={selectContentCls}>{sectors.filter(s => !(editForm.sectors || (editForm.sector ? editForm.sector.split(',').map(x => x.trim()).filter(Boolean) : [])).includes(s)).map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                    </Select>
                    <button onClick={() => setShowNewSector(true)} className="h-11 px-2.5 rounded-xl bg-white/5 text-white/40 hover:text-[#A38560]"><Plus size={16} /></button>
                  </div>
                ) : (
                  <div className="flex gap-1.5">
                    <Input value={newSectorName} onChange={e => setNewSectorName(e.target.value)} placeholder="Nuevo sector..." className={`${inputCls} flex-1`} onKeyDown={e => e.key === 'Enter' && createSector()} autoFocus />
                    <button onClick={createSector} className="h-11 px-2.5 rounded-xl bg-[#A38560] text-[#03110D]"><Plus size={16} /></button>
                    <button onClick={() => { setShowNewSector(false); setNewSectorName(''); }} className="h-11 px-2.5 rounded-xl bg-white/5 text-white/40"><X size={16} /></button>
                  </div>
                )}</div>
              <div><Label className="text-white/50 text-xs uppercase tracking-wider mb-1.5 block">Presupuesto</Label>
                <Input type="number" value={editForm.budget || ''} onChange={e => upd('budget', e.target.value)} className={inputCls} /></div>
              <div><Label className="text-white/50 text-xs uppercase tracking-wider mb-1.5 block">Habitaciones</Label>
                <Input type="number" value={editForm.rooms || ''} onChange={e => upd('rooms', e.target.value)} className={inputCls} /></div>
              <div><Label className="text-white/50 text-xs uppercase tracking-wider mb-1.5 block">Metraje Minimo (m²)</Label>
                <Input type="number" value={editForm.min_area || ''} onChange={e => upd('min_area', e.target.value ? parseFloat(e.target.value) : '')} placeholder="Ej: 300" className={inputCls} data-testid="lead-min-area" /></div>
              <div><Label className="text-white/50 text-xs uppercase tracking-wider mb-1.5 block">Tipo de Propiedad</Label>
                <Select value={editForm.property_type || ''} onValueChange={v => upd('property_type', v === '_none' ? '' : v)}>
                  <SelectTrigger className={inputCls} data-testid="lead-property-type"><SelectValue placeholder="Seleccionar..." /></SelectTrigger>
                  <SelectContent className={selectContentCls}>
                    <SelectItem value="_none">Sin especificar</SelectItem>
                    {['Casa','Apartamento','Local','Oficina','Lote','PH','Otro'].map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                  </SelectContent>
                </Select></div>
              <div><Label className="text-white/50 text-xs uppercase tracking-wider mb-1.5 block">Broker Asignado</Label>
                <Select value={editForm.assigned_broker_id || ''} onValueChange={v => { const b = brokers.find(x => x.id === v); upd('assigned_broker_id', v); upd('assigned_broker_name', b?.name || ''); }}>
                  <SelectTrigger className={inputCls}><SelectValue placeholder="Seleccionar..." /></SelectTrigger>
                  <SelectContent className={selectContentCls}>{brokers.map(b => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}</SelectContent>
                </Select></div>
              <div className="sm:col-span-2"><Label className="text-white/50 text-xs uppercase tracking-wider mb-1.5 block">Características deseadas</Label>
                <DesiredFeaturesPicker value={editForm.desired_features} onChange={v => upd('desired_features', v)} /></div>
              <div className="sm:col-span-2"><Label className="text-white/50 text-xs uppercase tracking-wider mb-1.5 block">Notas</Label>
                <Textarea data-testid="lead-notes" value={editForm.notes || ''} onChange={e => upd('notes', e.target.value)} className="bg-[#0A1F1A] border-[#1A3B32] text-white placeholder:text-white/30 focus:border-[#A38560] rounded-xl min-h-[80px]" /></div>
              {lead.lost_reason && (
                <div className="sm:col-span-2 p-3 bg-red-500/5 border border-red-500/10 rounded-xl">
                  <p className="text-xs text-red-400/60 uppercase tracking-wider mb-1">Motivo de Perdida</p>
                  <p className="text-sm text-red-400">{lead.lost_reason}</p>
                  <p className="text-xs text-white/30 mt-1">Desde etapa: {STAGES.find(s => s.key === lead.lost_origin_stage)?.label || lead.lost_origin_stage}</p>
                </div>
              )}
            </div>
          </div>
        </TabsContent>

        {/* TAREAS TAB */}
        <TabsContent value="tareas">
          <div className="bg-[#0A1F1A] rounded-2xl border border-white/5 p-4 lg:p-6 mt-4 space-y-4">
            <h3 className="font-outfit text-lg font-medium text-white">Tareas</h3>
            <div className="flex flex-col sm:flex-row gap-2">
              <Input value={newTask.title} onChange={e => setNewTask({ ...newTask, title: e.target.value })} placeholder="Nueva tarea..." className={`${inputCls} flex-1`} />
              <Input type="date" value={newTask.due_date} onChange={e => setNewTask({ ...newTask, due_date: e.target.value })} className={`${inputCls} w-40`} />
              <button onClick={addTask} className="bg-[#16302B] hover:bg-[#1A3B32] text-white px-4 py-2.5 rounded-xl border border-white/10 text-sm">Crear</button>
            </div>
            <div className="space-y-2">
              {tasks.map(t => {
                const isOverdue = t.due_date && !t.completed && new Date(t.due_date) < new Date();
                const dueFormatted = t.due_date ? new Date(t.due_date + 'T12:00:00').toLocaleDateString('es-CO', { day: 'numeric', month: 'short', year: 'numeric' }) : '';
                const createdFormatted = t.created_at ? new Date(t.created_at).toLocaleDateString('es-CO', { day: 'numeric', month: 'short' }) : '';
                return (
                  <div key={t.id} className={`flex items-start gap-3 p-3 rounded-xl border ${isOverdue ? 'bg-red-500/[0.04] border-red-500/15' : 'bg-[#16302B] border-white/5'}`}>
                    <button onClick={() => toggleTask(t)} data-testid={`task-toggle-${t.id}`} className="mt-0.5 shrink-0">
                      {t.completed ? <Check size={18} className="text-green-400" /> : <div className="w-[18px] h-[18px] rounded border border-white/30" />}
                    </button>
                    <div className="flex-1 min-w-0">
                      <p className={`text-sm ${t.completed ? 'text-white/40 line-through' : 'text-white'}`}>{t.title}</p>
                      {t.description && <p className="text-xs text-white/30 mt-0.5">{t.description}</p>}
                      <div className="flex items-center gap-2 mt-1.5">
                        {t.auto_generated && <span className="text-[10px] px-1.5 py-0.5 rounded bg-purple-500/10 text-purple-400">Auto</span>}
                        {createdFormatted && <span className="text-[10px] text-white/20">Creada: {createdFormatted}</span>}
                      </div>
                    </div>
                    {dueFormatted && (
                      <div className={`shrink-0 text-right px-2.5 py-1.5 rounded-lg border ${isOverdue ? 'bg-red-500/10 border-red-500/20' : t.completed ? 'bg-green-500/5 border-green-500/10' : 'bg-[#A38560]/10 border-[#A38560]/20'}`}>
                        <p className="text-[9px] uppercase tracking-wider text-white/30 mb-0.5">{isOverdue ? 'Vencida' : 'Vence'}</p>
                        <p className={`text-xs font-semibold ${isOverdue ? 'text-red-400' : t.completed ? 'text-green-400/60' : 'text-[#A38560]'}`}>{dueFormatted}</p>
                      </div>
                    )}
                  </div>
                );
              })}
              {tasks.length === 0 && <p className="text-sm text-white/40">No hay tareas.</p>}
            </div>
          </div>
        </TabsContent>

        {/* INMUEBLES TAB */}
        <TabsContent value="inmuebles">
          <div className="bg-[#0A1F1A] rounded-2xl border border-white/5 p-4 lg:p-6 mt-4 space-y-4">
            <h3 className="font-outfit text-lg font-medium text-white">Inmuebles Enviados</h3>
            <div className="flex gap-2">
              <Input value={newPropUrl} onChange={e => setNewPropUrl(e.target.value)} placeholder="URL del inmueble..." className={`${inputCls} flex-1`} />
              <button onClick={addProp} className="bg-[#16302B] hover:bg-[#1A3B32] text-white px-4 py-2.5 rounded-xl border border-white/10 text-sm">Agregar</button>
            </div>
            {properties.map(p => (
              <div key={p.id} className="p-3 bg-[#16302B] rounded-xl border border-white/5 flex items-start gap-3">
                <div className="flex-1 min-w-0">
                  <a href={p.url} target="_blank" rel="noreferrer" className="text-sm text-[#A38560] hover:underline break-all">{p.url}</a>
                  <p className="text-xs text-white/30 mt-1">{new Date(p.created_at).toLocaleDateString('es-CO')}</p>
                </div>
                <div className="flex items-center gap-1.5 shrink-0">
                  {['Aprobado', 'Visitado', 'Descartado'].map(st => {
                    const colors = { Aprobado: 'bg-green-500/10 border-green-500/20 text-green-400', Visitado: 'bg-blue-500/10 border-blue-500/20 text-blue-400', Descartado: 'bg-red-500/10 border-red-500/20 text-red-400' };
                    const isActive = p.status === st;
                    return (
                      <button key={st} onClick={async () => { await api.put(`/crm/leads/${id}/properties/${p.id}`, { status: st }); loadExtras(); }}
                        className={`text-[10px] px-2 py-1 rounded-lg border transition-all ${isActive ? colors[st] : 'bg-white/5 border-white/10 text-white/30 hover:text-white/60'}`}>
                        {st}
                      </button>
                    );
                  })}
                  <button onClick={async () => { await api.delete(`/crm/leads/${id}/properties/${p.id}`); loadExtras(); }}
                    className="p-1.5 rounded-lg hover:bg-red-500/10 text-white/20 hover:text-red-400"><Trash2 size={14} /></button>
                </div>
              </div>
            ))}
            {properties.length === 0 && <p className="text-sm text-white/40">No hay inmuebles agregados.</p>}
          </div>
        </TabsContent>

        {/* NEGOCIOS TAB */}
        <TabsContent value="negocios">
          <div className="bg-[#0A1F1A] rounded-2xl border border-white/5 p-4 lg:p-6 mt-4 space-y-4">
            <h3 className="font-outfit text-lg font-medium text-white">Negocios Vinculados ({linkedDeals.length})</h3>
            {linkedDeals.map(d => (
              <div key={d.id} onClick={() => navigate(`/negocios/${d.id}`)} className="p-4 bg-[#16302B] rounded-xl border border-white/5 cursor-pointer hover:border-[#A38560]/30 transition-colors">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-sm font-medium text-white">{d.property_address || 'Sin direccion'}</p>
                  <span className="text-xs px-2 py-0.5 rounded-full bg-[#A38560]/10 text-[#A38560]">{d.type}</span>
                </div>
                {d.closing_value && <p className="text-sm text-[#A38560] font-medium tabular-nums">{formatCOP(d.closing_value)}</p>}
              </div>
            ))}
            {linkedDeals.length === 0 && <p className="text-sm text-white/40">No hay negocios vinculados.</p>}
          </div>
        </TabsContent>

        {/* ACTIVIDAD TAB (LOG) */}
        <TabsContent value="actividad">
          <div className="bg-[#0A1F1A] rounded-2xl border border-white/5 p-4 lg:p-6 mt-4 space-y-4">
            {/* Activity Creation Form */}
            {!['cerrado', 'perdido'].includes(lead.stage) && (
              <div className="space-y-3 pb-4 border-b border-white/5">
                <div className="flex items-center gap-2">
                  <div className="flex items-center gap-1 bg-[#16302B] rounded-xl border border-white/5 p-0.5">
                    <button onClick={() => setActivityType('contact_attempt')} data-testid="type-contact-attempt"
                      className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs transition-colors ${activityType === 'contact_attempt' ? 'bg-red-500/15 text-red-400 font-medium border border-red-500/20' : 'text-white/50 hover:text-white'}`}>
                      <Phone size={12} /> Intento de contacto
                    </button>
                    <button onClick={() => setActivityType('note')} data-testid="type-note"
                      className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs transition-colors ${activityType === 'note' ? 'bg-[#A38560]/15 text-[#A38560] font-medium border border-[#A38560]/20' : 'text-white/50 hover:text-white'}`}>
                      <FileText size={12} /> Notas
                    </button>
                  </div>
                  {activityType === 'contact_attempt' && maxAttempts > 0 && (
                    <span className="text-[10px] text-white/30">{attemptCount}/{maxAttempts} intentos</span>
                  )}
                </div>
                <Textarea value={activityMessage} onChange={e => setActivityMessage(e.target.value)} data-testid="activity-message"
                  placeholder={activityType === 'contact_attempt' ? 'Ej: Se le escribio por WhatsApp, le envié opciones pero dijo que le escribiera en 15 dias...' : 'Escribe una nota sobre este lead...'}
                  className="bg-[#16302B] border-[#1A3B32] text-white placeholder:text-white/20 focus:border-[#A38560] rounded-xl min-h-[70px] text-sm" />
                {/* Templates dropdown */}
                <div className="flex flex-wrap items-center gap-2 pt-1">
                  <span className="text-[10px] text-white/40 uppercase tracking-wider">Plantillas:</span>
                  {templates.length > 0 ? (
                    templates.slice(0, 8).map(tpl => (
                      <div key={tpl.id} className="inline-flex items-center gap-1 group">
                        <button onClick={() => applyTemplate(tpl)} data-testid={`apply-tpl-${tpl.id}`}
                          className="text-[11px] px-2 py-1 rounded-full bg-white/5 hover:bg-[#A38560]/15 text-white/60 hover:text-[#A38560] border border-white/5"
                          title={tpl.content}>
                          {tpl.is_official && <span className="mr-1 text-[#A38560]">★</span>}
                          {tpl.title}
                        </button>
                        <button onClick={() => deleteTemplate(tpl.id)} className="text-[10px] text-white/20 hover:text-red-400 opacity-0 group-hover:opacity-100">×</button>
                      </div>
                    ))
                  ) : <span className="text-[11px] text-white/30 italic">Ninguna aun</span>}
                  <button onClick={() => setShowTplModal(true)} className="text-[11px] text-[#A38560] hover:underline ml-auto" data-testid="new-tpl-btn">+ Nueva plantilla</button>
                </div>
                <button onClick={addActivity} disabled={activityType === 'contact_attempt' && allAttemptsUsed} data-testid="add-activity-btn"
                  className={`text-sm font-medium px-4 py-2 rounded-xl transition-colors w-full sm:w-auto disabled:opacity-40 ${activityType === 'contact_attempt' ? 'bg-red-500/10 border border-red-500/20 text-red-400 hover:bg-red-500/20' : 'bg-[#A38560]/10 border border-[#A38560]/20 text-[#A38560] hover:bg-[#A38560]/20'}`}>
                  {activityType === 'contact_attempt' ? 'Registrar Intento' : 'Agregar Nota'}
                </button>
              </div>
            )}

            {/* Activity Log */}
            <h3 className="font-outfit text-sm font-medium text-white/60 uppercase tracking-wider">Historial</h3>
            <div className="space-y-1">
              {(lead.activity_log || []).slice().reverse().map((entry, i) => {
                const iconMap = {
                  stage_change: <ArrowRight size={14} className="text-blue-400" />,
                  contact_success: <CheckCircle2 size={14} className="text-green-400" />,
                  contact_attempt: <Phone size={14} className="text-red-400" />,
                  failed_attempt: <Phone size={14} className="text-red-400" />,
                  note: <FileText size={14} className="text-[#A38560]" />,
                  lost: <AlertTriangle size={14} className="text-red-400" />,
                  task_created: <Clock size={14} className="text-purple-400" />,
                  broker_change: <User size={14} className="text-yellow-400" />,
                  visit: <Eye size={14} className="text-emerald-400" />,
                  offer: <MessageSquare size={14} className="text-orange-400" />,
                  deal: <Briefcase size={14} className="text-[#A38560]" />,
                };
                const bgMap = {
                  stage_change: 'border-blue-500/20',
                  contact_success: 'border-green-500/20',
                  contact_attempt: 'border-red-500/20',
                  failed_attempt: 'border-red-500/20',
                  note: 'border-[#A38560]/20',
                  lost: 'border-red-500/30',
                  visit: 'border-emerald-500/20',
                  offer: 'border-orange-500/20',
                  deal: 'border-[#A38560]/30',
                };
                const isAttempt = entry.type === 'contact_attempt';
                const responded = entry.responded;
                const hasDetails = entry.details && Object.keys(entry.details).length > 0;
                const isExpandable = hasDetails || ['contact_attempt', 'note', 'visit', 'offer', 'deal', 'lost'].includes(entry.type);
                const isExpanded = expandedEntry === (entry.id || i);
                const entryKey = entry.id || i;
                return (
                  <div key={entryKey} className={`border-l-2 ${bgMap[entry.type] || 'border-white/10'} bg-[#16302B]/50 rounded-r-lg overflow-hidden transition-all`}>
                    <div className={`flex items-start gap-3 p-3 ${isExpandable ? 'cursor-pointer hover:bg-white/[0.02]' : ''}`}
                      onClick={() => isExpandable && setExpandedEntry(isExpanded ? null : entryKey)}>
                      <div className="mt-0.5 shrink-0">{iconMap[entry.type] || <Clock size={14} className="text-white/30" />}</div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm text-white/80">{entry.message}</p>
                        <div className="flex items-center gap-2 mt-1">
                          <span className="text-[10px] text-white/30">{entry.timestamp ? new Date(entry.timestamp).toLocaleString('es-CO', { timeZone: 'America/Bogota' }) : ''}</span>
                          {entry.by && <span className="text-[10px] text-white/20">— {entry.by}</span>}
                        </div>
                      </div>
                      {isAttempt && (
                        <button onClick={(e) => { e.stopPropagation(); if (!responded) toggleResponded(entry.id); }} data-testid={`respond-${entry.id}`}
                          className={`shrink-0 flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-[10px] font-medium transition-all ${responded ? 'bg-green-500/10 border border-green-500/20 text-green-400 cursor-default' : 'bg-white/5 border border-white/10 text-white/40 hover:border-green-500/30 hover:text-green-400 hover:bg-green-500/5 cursor-pointer'}`}
                          title={responded ? 'Cliente respondio' : 'Marcar como respondio'}>
                          {responded ? <CheckCircle2 size={14} /> : <Circle size={14} />}
                          <span>{responded ? 'Respondio' : 'Respondio?'}</span>
                        </button>
                      )}
                      {isExpandable && <ChevronDown size={14} className={`shrink-0 text-white/20 transition-transform ${isExpanded ? 'rotate-180' : ''}`} />}
                      {entry.id && <button onClick={(e) => { e.stopPropagation(); deleteActivity(entry.id); }}
                        className="shrink-0 p-1 rounded hover:bg-red-500/10 text-white/10 hover:text-red-400 transition-colors"><Trash2 size={12} /></button>}
                    </div>
                    {isExpanded && (
                      <div className="px-3 pb-3 pl-10">
                        <ActivityDetails entry={entry} />
                      </div>
                    )}
                  </div>
                );
              })}
              {(!lead.activity_log || lead.activity_log.length === 0) && <p className="text-sm text-white/40">No hay actividad registrada.</p>}
            </div>
          </div>
        </TabsContent>

        <TabsContent value="listings">
          <LeadListings leadId={id} refreshKey={[lead.client_type, lead.budget, lead.rooms, lead.min_area, (lead.sectors || []).join('|'), lead.sector, (lead.desired_features || []).join('|')].join('~')} />
        </TabsContent>
      </Tabs>

      {/* Lost Modal */}
      <Dialog open={lostModal} onOpenChange={setLostModal}><DialogContent className="bg-[#0A1F1A] border-[#1A3B32] text-white max-w-md">
        <DialogHeader><DialogTitle className="font-outfit text-lg flex items-center gap-2"><AlertTriangle size={20} className="text-red-400" /> Mover a Perdido</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div><Label className="text-white/50 text-xs uppercase tracking-wider mb-1.5 block">Motivo de Perdida *</Label>
            <Select value={lostReason} onValueChange={setLostReason}>
              <SelectTrigger className={inputCls} data-testid="lost-reason-detail"><SelectValue placeholder="Selecciona un motivo..." /></SelectTrigger>
              <SelectContent className={selectContentCls}>{LOST_REASONS.map(r => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
            </Select></div>
          <div><Label className="text-white/50 text-xs uppercase tracking-wider mb-1.5 block">Etapa de Origen</Label>
            <Input value={STAGES.find(s => s.key === lead.stage)?.label || ''} className={`${inputCls} opacity-60`} disabled /></div>
        </div>
        <DialogFooter className="gap-2">
          <button onClick={() => setLostModal(false)} className="px-4 py-2.5 rounded-xl border border-white/10 text-white/60 text-sm">Cancelar</button>
          <button onClick={confirmLost} data-testid="confirm-lost-detail-btn" className="bg-red-500 text-white font-semibold px-6 py-2.5 rounded-xl text-sm">Confirmar</button>
        </DialogFooter>
      </DialogContent></Dialog>

      {/* New template modal */}
      <Dialog open={showTplModal} onOpenChange={setShowTplModal}>
        <DialogContent className="bg-[#03110D] border-white/10 max-w-md">
          <DialogHeader><DialogTitle className="text-white">Nueva plantilla de actividad</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-white/50 text-xs uppercase tracking-wider mb-1.5 block">Titulo corto</Label>
              <Input value={newTpl.title} onChange={e => setNewTpl({ ...newTpl, title: e.target.value })} className={inputCls} placeholder="Ej: No responde whatsapp" data-testid="tpl-title" />
            </div>
            <div>
              <Label className="text-white/50 text-xs uppercase tracking-wider mb-1.5 block">Tipo</Label>
              <select value={newTpl.type} onChange={e => setNewTpl({ ...newTpl, type: e.target.value })}
                className={`${inputCls} px-3 py-2`}>
                <option value="note">Nota</option>
                <option value="contact_attempt">Intento de contacto</option>
              </select>
            </div>
            <div>
              <Label className="text-white/50 text-xs uppercase tracking-wider mb-1.5 block">Contenido</Label>
              <Textarea value={newTpl.content} onChange={e => setNewTpl({ ...newTpl, content: e.target.value })}
                className="bg-[#16302B] border-[#1A3B32] text-white rounded-xl min-h-[100px] text-sm" placeholder="Texto que se rellenara al aplicar la plantilla..." data-testid="tpl-content" />
            </div>
            {user?.role === 'admin' && (
              <label className="flex items-center gap-2 text-xs text-white/70 cursor-pointer">
                <input type="checkbox" checked={!!newTpl.is_official} onChange={e => setNewTpl({ ...newTpl, is_official: e.target.checked })} />
                Marcar como plantilla oficial (visible para todos)
              </label>
            )}
          </div>
          <DialogFooter className="gap-2">
            <button onClick={() => setShowTplModal(false)} className="px-4 py-2.5 rounded-xl border border-white/10 text-white/60 text-sm">Cancelar</button>
            <button onClick={async () => { await saveTemplate(); setShowTplModal(false); }} className="bg-[#A38560] text-[#03110D] font-semibold px-6 py-2.5 rounded-xl text-sm" data-testid="save-tpl-btn">Guardar</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function ActivityDetails({ entry }) {
  const d = entry.details;
  const labelCls = "text-[10px] text-white/30 uppercase tracking-wider";
  const valCls = "text-xs text-white/70";

  if (d && Object.keys(d).length > 0) {
    const DETAIL_LABELS = {
      propietario: 'Propietario', direccion: 'Direccion', fecha: 'Fecha',
      comentarios: 'Comentarios', tipo: 'Tipo', valor_cierre: 'Valor de Cierre',
      valor_publicado: 'Valor Publicado', sector: 'Sector', fecha_cierre: 'Fecha de Cierre',
      broker_cliente: 'Broker Cliente', broker_propietario: 'Broker Propietario',
    };
    const entries = Object.entries(d).filter(([, v]) => v !== '' && v !== null && v !== undefined && v !== 0);
    if (entries.length === 0) return <p className="text-xs text-white/30 italic">Sin detalles adicionales</p>;
    return (
      <div className="grid grid-cols-2 gap-x-4 gap-y-2 bg-[#0A1F1A]/50 rounded-lg p-3 border border-white/5" data-testid="activity-details">
        {entries.map(([key, val]) => (
          <div key={key}>
            <p className={labelCls}>{DETAIL_LABELS[key] || key}</p>
            <p className={valCls}>{typeof val === 'number' ? `$${val.toLocaleString('es-CO')}` : val}</p>
          </div>
        ))}
      </div>
    );
  }

  // For entries without details object, show contextual info
  if (entry.type === 'contact_attempt' || entry.type === 'failed_attempt') {
    return (
      <div className="bg-[#0A1F1A]/50 rounded-lg p-3 border border-white/5 space-y-1" data-testid="activity-details">
        <div><p className={labelCls}>Mensaje</p><p className={valCls}>{entry.message}</p></div>
        {entry.responded_at && <div><p className={labelCls}>Respondio el</p><p className={valCls}>{new Date(entry.responded_at).toLocaleString('es-CO', { timeZone: 'America/Bogota' })}</p></div>}
      </div>
    );
  }
  if (entry.type === 'note') {
    return (
      <div className="bg-[#0A1F1A]/50 rounded-lg p-3 border border-white/5" data-testid="activity-details">
        <p className={valCls}>{entry.message}</p>
      </div>
    );
  }
  if (entry.type === 'lost') {
    return (
      <div className="bg-[#0A1F1A]/50 rounded-lg p-3 border border-white/5" data-testid="activity-details">
        <p className={valCls}>{entry.message}</p>
      </div>
    );
  }
  return <p className="text-xs text-white/30 italic">Sin detalles adicionales</p>;
}
